/**
 * File: generationService.ts
 * Description: Service class for generation-related API operations
 * Author: GitHub Copilot
 * Created: 2025-08-01
 * Last Modified: 2025-08-01
 */

import { API_CONFIG } from '../config/api';
import { AsyncJobStartResponse } from '../types/asyncJob';
import { 
  ArchitectureSection,
  GenerateRequest,
  GenerationResponse,
  SectionContentRequest,
  SectionResponse, 
} from '../types/generation';

import { apiClient } from './apiClient';
import { startAndPollJob } from './jobPollingService';

export class GenerationService {
  private static readonly EXPORT_CONTENT_BASE64_KEY = 'content_base64';
  private static readonly EXPORT_MEDIA_TYPE_KEY = 'media_type';

  /**
   * Generate a specific section for a project.
   *
   * Runs as a background job to avoid platform request timeouts: it starts
   * the job, then polls until the section has been generated.
   *
   * @param projectId - The ID of the project
   * @param input - The user input for generation
   * @param section - The section to generate
   * @return Promise with generation response
   * @throws ApiError on request failure
   */
  public async generateSection(
    projectId: number, 
    input: string, 
    section: ArchitectureSection,
  ): Promise<GenerationResponse> {
    const request: GenerateRequest = {
      input,
      section,
    };

    return startAndPollJob<GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/generate/async`,
          request,
        ),
    );
  }

  /**
   * Build a start-factory that kicks off section generation as a background
   * job and resolves with the job id. Used with the navigation-resilient
   * agent-job tracker, which owns the polling so the run survives page
   * navigation and full reloads.
   *
   * @param projectId - The ID of the project
   * @param input - The user input for generation
   * @param section - The section to generate
   * @return A function that issues the start request (HTTP 202)
   */
  public startGenerateSection(
    projectId: number,
    input: string,
    section: ArchitectureSection,
  ): () => Promise<AsyncJobStartResponse> {
    const request: GenerateRequest = {
      input,
      section,
    };

    return () =>
      apiClient.post<AsyncJobStartResponse>(
        `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/generate/async`,
        request,
      );
  }

  /**
   * Get a specific generated section for a project
   * @param projectId - The ID of the project
   * @param section - The section to retrieve
   * @return Promise with section response
   * @throws ApiError on request failure
   */
  public async getSection(
    projectId: number, 
    section: ArchitectureSection,
  ): Promise<SectionResponse> {
    return apiClient.get<SectionResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/section/${section}`,
    );
  }

  public async getSectionExecutionPlan(
    projectId: number, 
    section: ArchitectureSection,
  ): Promise<Array<Record<string, any>> | Array<string> | null> {
    return apiClient.get<Array<Record<string, any>> | Array<string> | null>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/section/${section}/execution_plan`,
    );
  }

  public async getSectionGroundedness(
    projectId: number, 
    section: ArchitectureSection,
  ): Promise<{ score: number; ungrounded_items: Record<string, string> } | null> {
    return apiClient.get<{ score: number; ungrounded_items: Record<string, string> } | null>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/section/${section}/groundedness`,
    );
  }

  /**
   * Update a specific generated section for a project
   * @param projectId - The ID of the project
   * @param section - The section to update
   * @param content - The updated content
   * @return Promise with section response
   * @throws ApiError on request failure
   */
  public async updateSection(
    projectId: number, 
    section: ArchitectureSection,
    content: string,
  ): Promise<SectionResponse> {
    const request: SectionContentRequest = {
      content,
    };
    
    return apiClient.post<SectionResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/section/${section}`,
      request,
    );
  }

  /**
   * Edit a section with custom instructions using SectionEditorAgent
   * This is faster than full regeneration for iterative improvements
   * @param projectId - The ID of the project
   * @param section - The section to edit
   * @param instructions - User's edit instructions
   * @return Promise with generation response
   * @throws ApiError on request failure
   */
  public async editSection(
    projectId: number,
    section: ArchitectureSection,
    instructions: string,
  ): Promise<GenerationResponse> {
    const request = {
      instructions,
    };

    return startAndPollJob<GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}` +
            `/section/${section}/edit/async`,
          request,
        ),
    );
  }

  /**
   * Export the full architecture document as a downloadable file
   * @param projectId - The ID of the project
   * @param format - The export format (markdown, pdf, docx)
   * @return Promise with Blob containing the exported document
   * @throws ApiError on request failure
   */
  public async exportDocument(
    projectId: number,
    format: string,
  ): Promise<Blob> {
    const result = await startAndPollJob<Record<string, string>>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/export/async` +
            `?format=${encodeURIComponent(format)}`,
          {},
        ),
    );

    const contentBase64 =
      result[GenerationService.EXPORT_CONTENT_BASE64_KEY] ?? '';
    const mediaType =
      result[GenerationService.EXPORT_MEDIA_TYPE_KEY] ??
      'application/octet-stream';
    return this.decodeBase64ToBlob(contentBase64, mediaType);
  }

  private decodeBase64ToBlob(contentBase64: string, mediaType: string): Blob {
    const decoded = atob(contentBase64);
    const bytes = new Uint8Array(decoded.length);
    for (let idx = 0; idx < decoded.length; idx += 1) {
      bytes[idx] = decoded.codePointAt(idx) ?? 0;
    }
    return new Blob([bytes], { type: mediaType });
  }
}

export const generationService = new GenerationService();
